<template>
  <v-card outlined class="sales-card">
    <v-card-text class="pa-4">
      <div class="sales-header">
        <div>
          <div class="sales-title">Sales Overview</div>
          <div class="sales-subtitle">Current year vs last year</div>
        </div>

        <div class="avg-sale-box">
          <div class="avg-label">Avg Monthly Sale</div>
          <div class="avg-value">{{ avgSale | currency }}</div>
        </div>
      </div>

      <v-skeleton-loader v-if="loading" type="card" class="mt-4" />

      <div v-else>
        <v-row dense class="mt-4">
          <v-col cols="12" sm="4">
            <div class="summary-box">
              <div class="summary-label">Current Year</div>
              <div class="summary-value">{{ currentYearTotal | currency }}</div>
            </div>
          </v-col>

          <v-col cols="12" sm="4">
            <div class="summary-box">
              <div class="summary-label">Last Year</div>
              <div class="summary-value">{{ lastYearTotal | currency }}</div>
            </div>
          </v-col>

          <v-col cols="12" sm="4">
            <div class="summary-box">
              <div class="summary-label">Difference</div>
              <div
                class="summary-value"
                :class="difference >= 0 ? 'positive-value' : 'negative-value'"
              >
                {{ difference | currency }}
              </div>
            </div>
          </v-col>
        </v-row>

        <div :id="chartId" class="sales-chart mt-4"></div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import homeService from "@/modules/Home/service";
import Highcharts from "highcharts";
import { mapGetters } from "vuex";

export default {
  name: "SaleCount",

  data() {
    return {
      loading: false,
      avgSale: 0,
      currentYearTotal: 0,
      lastYearTotal: 0,
      difference: 0,
      chartId: `sales-chart-${Math.random().toString(36).substr(2, 9)}`,
      months: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
    };
  },

  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),
  },

  mounted() {
    this.init();
  },

  methods: {
    async init() {
      this.loading = true;

      const salesData = await this.loadSalesData();

      await this.averageSaleByPeriod();

      this.loading = false;

      this.$nextTick(() => {
        if (salesData) {
          this.renderChart(salesData.currentYear, salesData.lastYear);
        }
      });
    },

    async averageSaleByPeriod() {
      try {
        const response = await homeService.averageSaleByPeriod({
          orderBy: "month",
        });

        this.avgSale = response.data
          ? Number(response.data.averageMonthlySales || 0)
          : 0;
      } catch (error) {
        this.avgSale = 0;
      }
    },

    async loadSalesData() {
      try {
        const response = await homeService.getSalesByPeriod({
          orderBy: "month",
        });

        const currentYear = new Array(12).fill(0);
        const lastYear = new Array(12).fill(0);

        const currentRows =
          response.data &&
          response.data.currentMonth &&
          response.data.currentMonth[0]
            ? response.data.currentMonth[0]
            : [];

        const lastRows =
          response.data && response.data.lastMonth && response.data.lastMonth[0]
            ? response.data.lastMonth[0]
            : [];

        currentRows.forEach((row) => {
          const index = Number(row.date) - 1;
          if (index >= 0 && index <= 11) {
            currentYear[index] = Number(row.total_sales || 0);
          }
        });

        lastRows.forEach((row) => {
          const index = Number(row.date) - 1;
          if (index >= 0 && index <= 11) {
            lastYear[index] = Number(row.total_sales || 0);
          }
        });

        this.currentYearTotal = currentYear.reduce((sum, value) => {
          return sum + Number(value || 0);
        }, 0);

        this.lastYearTotal = lastYear.reduce((sum, value) => {
          return sum + Number(value || 0);
        }, 0);

        this.difference = this.currentYearTotal - this.lastYearTotal;

        return {
          currentYear,

          lastYear,
        };
      } catch (error) {
        this.currentYearTotal = 0;
        this.lastYearTotal = 0;
        this.difference = 0;

        return {
          currentYear: new Array(12).fill(0),
          lastYear: new Array(12).fill(0),
        };
      }
    },

    renderChart(currentYear, lastYear) {
      Highcharts.chart(this.chartId, {
        credits: {
          enabled: false,
        },

        chart: {
          type: "areaspline",
          height: 320,
          backgroundColor: "transparent",
          spacingTop: 10,
          spacingRight: 10,
          spacingBottom: 10,
          spacingLeft: 0,
        },

        title: {
          text: null,
        },

        xAxis: {
          categories: this.months,
          tickmarkPlacement: "on",
          lineColor: "#e2e8f0",
          tickColor: "#e2e8f0",
          labels: {
            style: {
              color: "#64748b",
              fontSize: "12px",
            },
          },
        },

        yAxis: {
          title: {
            text: null,
          },
          gridLineColor: "#e2e8f0",
          labels: {
            style: {
              color: "#64748b",
              fontSize: "12px",
            },
            formatter: function () {
              return "$" + Highcharts.numberFormat(this.value / 1000, 0) + "k";
            },
          },
        },

        legend: {
          align: "right",
          verticalAlign: "top",
          itemStyle: {
            color: "#475569",
            fontWeight: "500",
          },
        },

        tooltip: {
          shared: true,
          useHTML: true,
          borderRadius: 12,
          formatter: function () {
            let html = `<strong>${this.x}</strong><br/>`;

            this.points.forEach((point) => {
              html += `
                <span>${point.series.name}</span>:
                <b>$${Highcharts.numberFormat(point.y, 2, ".", ",")}</b>
                <br/>
              `;
            });

            return html;
          },
        },

        plotOptions: {
          areaspline: {
            fillOpacity: 0.18,
            lineWidth: 3,
            marker: {
              radius: 4,
              symbol: "circle",
            },
          },
          series: {
            animation: {
              duration: 700,
            },
          },
        },

        series: [
          {
            name: "Last Year",
            data: lastYear,
            color: "#94a3b8",
          },
          {
            name: "Current Year",
            data: currentYear,
            color: "#2563eb",
          },
        ],
      });
    },
  },
};
</script>

<style scoped>
.sales-card {
  border-radius: 20px;
  background: #ffffff;
}

.sales-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.sales-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.sales-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.avg-sale-box {
  text-align: right;
  background: #f8fafc;
  border-radius: 16px;
  padding: 10px 14px;
  min-width: 180px;
}

.avg-label {
  font-size: 12px;
  color: #64748b;
}

.avg-value {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.summary-box {
  background: #f8fafc;
  border-radius: 16px;
  padding: 14px;
}

.summary-label {
  font-size: 12px;
  color: #64748b;
}

.summary-value {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin-top: 4px;
}

.positive-value {
  color: #16a34a;
}

.negative-value {
  color: #dc2626;
}

.sales-chart {
  min-height: 320px;
}
</style>